import { Spacing } from '@persona-id/design-tokens';
import { noop } from 'lodash';
import { createContext, FC, PropsWithChildren, ReactNode, useContext } from 'react';
import styled from 'styled-components';

import { getFromChildren } from 'dashboard/views/DashboardSARShowView/components/SectionedCard/helpers';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { isActive, isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import menuItemMixins from 'design-system/mixins/menuItem';
import { paragraphMixins } from 'design-system/mixins/text';
import { SessionStorageKey } from 'lib/clients/StorageClient';
import { useSessionStorageMap } from 'lib/hooks/useSessionStorageMap';

const SectionedCardContext = createContext({
  selectedSection: null,
  setSelectedSection: noop,
});

const SectionedCardContainer = styled.div`
  display: grid;
  grid-template-columns: 250px 1px 1fr;
`;

const SectionCard = styled(Card)`
  border-radius: 0;
  margin: 0;

  &:first-of-type {
    border-radius: ${({ theme }) => `${theme.borderRadius} 0 0 ${theme.borderRadius}`};
  }

  &:last-of-type {
    border-radius: ${({ theme }) => `0 ${theme.borderRadius} ${theme.borderRadius} 0`};
  }
`;

const VerticalDivider = styled.div`
  width: 1px;
  height: 100%;
  background-color: ${({ theme }) => theme.color.border.primary};
`;

const SectionNavItem = styled(Button).attrs(() => ({
  variant: 'primary',
  type: 'button',
}))<{ active: boolean }>`
  ${paragraphMixins.md};
  ${menuItemMixins.default};

  padding: ${Spacing[1]} ${Spacing[3]};
  color: ${({ theme }) => theme.color.text.secondary};

  & > div {
    justify-content: space-between;
  }

  &${isHovered}${isNotDisabled} {
    ${menuItemMixins.hover};
    box-shadow: none;
  }

  &${isFocused}${isNotDisabled} {
    box-shadow: none;
  }

  &${isActive}${isNotDisabled} {
    ${menuItemMixins.active};
    box-shadow: none;
  }

  // we need these to reset the default button styles
  &${isHovered}${isFocused}${isNotDisabled} {
    ${menuItemMixins.active};
    box-shadow: none;
  }
`;

const SectionContainer = styled.div<{ isHidden: boolean }>`
  display: ${({ isHidden }) => (isHidden ? 'none' : 'default')};
`;

export interface SectionedCardProps {
  children: ReactNode;
  scope?: string;
}

export function SectionedCard({ children, scope }: SectionedCardProps) {
  // this fetches childrens's props so we can use them in the parent 🧠
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const sectionProps = getFromChildren<SectionProps>(children, Section, (section) => section.props);

  const [selectedSection, setSelectedSection] = useSessionStorageMap<string>(
    SessionStorageKey.SectionedCardIdx,
    scope,
    sectionProps[0].title,
  );

  return (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    <SectionedCardContext.Provider value={{ selectedSection, setSelectedSection }}>
      <SectionedCardContainer>
        <SectionCard>
          <Card.Body>
            <div>
              {sectionProps.map(({ title, icon }) => {
                return (
                  <SectionNavItem
                    key={title}
                    role='tab'
                    id={`SectionedCard__nav--${title}`}
                    active={title === selectedSection}
                    onClick={() => {
                      setSelectedSection(title);
                    }}
                    suffixIcon={icon}
                  >
                    <span>{title}</span>
                  </SectionNavItem>
                );
              })}
            </div>
          </Card.Body>
        </SectionCard>
        <VerticalDivider />
        <SectionCard>
          <Card.Body>{children}</Card.Body>
        </SectionCard>
      </SectionedCardContainer>
    </SectionedCardContext.Provider>
  );
}

interface SectionProps {
  title: string;
  icon?: ReactNode;
}

export const Section: FC<PropsWithChildren<SectionProps>> = ({ children, title }) => {
  const { selectedSection } = useContext(SectionedCardContext);
  const isActive = selectedSection === title;

  // we hide the element with styling because a React unmount can cause form elements to unregister
  return <SectionContainer isHidden={!isActive}>{children}</SectionContainer>;
};
